import { computed, inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TourCompletionState, TourDefinition } from '../../models/tour/guided-tour.model';
import { UserProfileStore } from '../../states/user-profile/user-profile.store';
import {
  autoPromptTourForRoute,
  EMPLOYEE_PORTAL_TOUR,
  employeePortalTourForRoute,
} from './guided-tour.definitions';

export { EMPLOYEE_PORTAL_TOUR } from './guided-tour.definitions';

@Injectable({ providedIn: 'root' })
export class GuidedTourService {
  private readonly router = inject(Router);
  private readonly userProfileStore = inject(UserProfileStore);

  private readonly storagePrefix = 'trieonhr_tour';
  private readonly dismissedForSession = signal(false);
  private readonly currentUrl = signal(this.router.url);

  readonly activeTour = signal<TourDefinition | null>(null);
  readonly promptTour = signal<TourDefinition | null>(null);
  readonly currentStepIndex = signal(0);
  readonly promptVisible = signal(false);
  readonly repositionTick = signal(0);

  readonly isActive = computed(() => this.activeTour() !== null);

  readonly currentStep = computed(() => {
    const tour = this.activeTour();
    if (!tour) {
      return null;
    }
    return tour.steps[this.currentStepIndex()] ?? null;
  });

  readonly stepCount = computed(() => this.activeTour()?.steps.length ?? 0);
  readonly isFirstStep = computed(() => this.currentStepIndex() === 0);

  readonly isLastStep = computed(() => {
    const tour = this.activeTour();
    if (!tour) {
      return false;
    }
    return this.currentStepIndex() >= tour.steps.length - 1;
  });

  readonly contextualTour = computed(() => employeePortalTourForRoute(this.currentUrl()));

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => this.onRouteChange(event.urlAfterRedirects));

    this.updatePrompt(this.router.url);
  }

  startTour(tour: TourDefinition): void {
    this.promptVisible.set(false);
    this.dismissedForSession.set(false);
    this.activeTour.set(tour);
    this.currentStepIndex.set(0);
    void this.navigateToStep(tour.steps[0]);
  }

  replayEmployeePortalTour(): void {
    this.startTour(EMPLOYEE_PORTAL_TOUR);
  }

  next(): void {
    const tour = this.activeTour();
    if (!tour) {
      return;
    }

    if (this.isLastStep()) {
      this.completeTour();
      return;
    }

    const nextIndex = this.currentStepIndex() + 1;
    this.currentStepIndex.set(nextIndex);
    void this.navigateToStep(tour.steps[nextIndex]);
  }

  previous(): void {
    const tour = this.activeTour();
    if (!tour || this.isFirstStep()) {
      return;
    }

    const prevIndex = this.currentStepIndex() - 1;
    this.currentStepIndex.set(prevIndex);
    void this.navigateToStep(tour.steps[prevIndex]);
  }

  skip(): void {
    this.completeTour(true);
  }

  dismissPrompt(): void {
    this.dismissedForSession.set(true);
    this.promptVisible.set(false);
    this.promptTour.set(null);
  }

  requestReposition(): void {
    this.repositionTick.update((value) => value + 1);
  }

  private onRouteChange(url: string): void {
    this.currentUrl.set(url);

    const active = this.activeTour();
    if (active && !this.isEmployeePortalRoute(url)) {
      this.stopTour();
    }

    this.updatePrompt(url);
  }

  private updatePrompt(url: string): void {
    if (this.isActive() || this.dismissedForSession()) {
      return;
    }

    const autoTour = autoPromptTourForRoute(url);
    if (!autoTour || this.isTourCompleted(autoTour)) {
      this.promptVisible.set(false);
      this.promptTour.set(null);
      return;
    }

    this.promptTour.set(autoTour);
    this.promptVisible.set(true);
  }

  private async navigateToStep(step: TourDefinition['steps'][number]): Promise<void> {
    if (step.route) {
      const currentPath = this.router.url.split('?')[0].split('#')[0];
      if (currentPath !== step.route) {
        await this.router.navigateByUrl(step.route);
      }
    }

    queueMicrotask(() => this.requestReposition());
    window.setTimeout(() => this.requestReposition(), 120);
    window.setTimeout(() => this.requestReposition(), 320);
  }

  private completeTour(skipped = false): void {
    const tour = this.activeTour();
    if (tour && !skipped) {
      this.saveCompletion(tour);
    }
    this.stopTour();
  }

  private stopTour(): void {
    this.activeTour.set(null);
    this.currentStepIndex.set(0);
    this.dismissedForSession.set(false);
  }

  private isTourCompleted(tour: TourDefinition): boolean {
    const state = this.readState(tour);
    return state?.completed === true && state.version >= tour.version;
  }

  private saveCompletion(tour: TourDefinition): void {
    this.writeState(tour, {
      completed: true,
      completedAt: new Date().toISOString(),
      version: tour.version,
    });
  }

  private readState(tour: TourDefinition): TourCompletionState | null {
    const userGuid = this.userProfileStore.userProfile()?.userProfileGuid;
    if (!userGuid) {
      return null;
    }

    const raw = localStorage.getItem(this.buildKey(tour.id, userGuid));
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as TourCompletionState;
    } catch {
      return null;
    }
  }

  private writeState(tour: TourDefinition, state: TourCompletionState): void {
    const userGuid = this.userProfileStore.userProfile()?.userProfileGuid;
    if (!userGuid) {
      return;
    }

    localStorage.setItem(this.buildKey(tour.id, userGuid), JSON.stringify(state));
  }

  private buildKey(tourId: string, userGuid: string): string {
    return `${this.storagePrefix}_${tourId}_${userGuid}`;
  }

  private isEmployeePortalRoute(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path === '/me' || path.startsWith('/me/');
  }
}
